import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import PatientDetails from "./PatientDetails";
import SupportAgentChat from "./SupportAgentChat";
import SupportFloatingActions from "./SupportFloatingActions";
import { getPatientData } from "./patientApi";
import { getSharedSession } from "./ticketApi";
import { usePatientSupportData } from "./usePatientSupportData";
import { runValidators, supportValidators } from "./validators";
import {
  getSupportBackLabel,
  getSupportBackPath,
  supportHomePath,
} from "./supportUrls";
import { isCapabilityEnabled } from "../../config/capabilities";

const CHAT_SUGGESTIONS = [
  "Why is the note missing for this patient?",
  "Check the pipeline runs and explain any errors",
  "Does the transcript look complete?",
  "Draft a reply to the doctor about this patient's issue",
  ...(isCapabilityEnabled("smartChart")
    ? ["Check Smart Chart additional context and the custom prompt"]
    : []),
];

const SupportPatientPage = () => {
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const shareId = searchParams.get("share");

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientRefreshKey, setPatientRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lookupError, setLookupError] = useState(null);

  // Shared session restore
  const [share, setShare] = useState(shareId ? { loading: true } : null);

  const supportData = usePatientSupportData(selectedPatient, patientRefreshKey);
  const backPath = getSupportBackPath(searchParams);
  const backLabel = getSupportBackLabel(searchParams);

  // Validator results serialized for the agent chat seed context. Runs the
  // same validators ValidatorResults renders, once the support data is in.
  const [validatorSummary, setValidatorSummary] = useState(null);
  const { validationContext } = supportData;

  useEffect(() => {
    const patientIdForRun = validationContext?.patient?.patient_id;
    if (!patientIdForRun || validationContext.isLoading) {
      setValidatorSummary(null);
      return;
    }

    let cancelled = false;
    (async () => {
      const results = await runValidators(supportValidators, validationContext);
      if (cancelled) return;
      setValidatorSummary({
        patient_id: String(patientIdForRun),
        results: results.map((r) => ({
          id: r.test.id,
          name: r.test.name,
          status: r.status,
          messages: r.messages,
        })),
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [validationContext]);

  // Everything the page already knows, sent with the first chat message so
  // the agent doesn't need to re-look anything up. Kept slim — content
  // artifacts (note/transcript/chart/Smart Chart text) are fetched
  // server-side on demand.
  const chatSeedContext = useMemo(() => {
    if (!selectedPatient?.patient_id) return null;
    const doctor = supportData.doctor;
    return {
      patient: {
        patient_id: selectedPatient.patient_id,
        description: selectedPatient.description,
        mrn: selectedPatient.mrn,
        patient_type: selectedPatient.patient_type,
        date_of_appt: selectedPatient.date_of_appt,
        appt_time: selectedPatient.appt_time,
        import_type: selectedPatient.import_type,
        ehr_upload_status: selectedPatient.ehr_upload_status,
        status: selectedPatient.status,
        note: Boolean(selectedPatient.note),
        chart_history: Boolean(selectedPatient.chart_history),
      },
      doctor: doctor
        ? {
            user_id: doctor.user_id,
            user_name: doctor.user_name,
            user_email: doctor.user_email,
            organization: doctor.organization,
            super_org: doctor.super_org,
            specialty: doctor.specialty,
            model_type: doctor.model_type,
          }
        : null,
      validator_results: validatorSummary ? [validatorSummary] : [],
      // Flags only — additional context / custom prompt text is fetched
      // on demand via get_patient_smart_chart.
      smartChart:
        isCapabilityEnabled("smartChart") && supportData.smartChart
          ? {
              smart_chart: supportData.smartChart.smart_chart === true,
              has_additional_context:
                supportData.smartChart.has_additional_context === true,
              has_custom_prompt:
                supportData.smartChart.has_custom_prompt === true,
            }
          : undefined,
    };
  }, [
    selectedPatient,
    supportData.doctor,
    supportData.smartChart,
    validatorSummary,
  ]);

  const loadPatient = useCallback(async (id) => {
    setIsLoading(true);
    setLookupError(null);
    try {
      const patient = await getPatientData(id);
      if (patient && !patient.error && Object.keys(patient).length > 0) {
        setSelectedPatient(patient);
      } else {
        setSelectedPatient(null);
        setLookupError("Patient not found. Please check the ID and try again.");
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (patientId) {
      loadPatient(patientId);
    }
  }, [patientId, loadPatient]);

  useEffect(() => {
    if (!shareId) return;
    let cancelled = false;
    (async () => {
      const data = await getSharedSession(shareId);
      if (cancelled) return;
      if (data.error) {
        setShare({ error: data.error });
        return;
      }
      setShare({ data });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareId]);

  const handleRefreshPatient = async () => {
    const id = selectedPatient?.patient_id;
    if (id == null || isRefreshing) return;

    setIsRefreshing(true);
    setLookupError(null);

    try {
      const patient = await getPatientData(id);
      if (patient && !patient.error && Object.keys(patient).length > 0) {
        setSelectedPatient(patient);
        setPatientRefreshKey((key) => key + 1);
      } else {
        setLookupError("Failed to reload patient. Please try again.");
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleChangePatient = () => {
    navigate(backPath);
  };

  if (isLoading && !selectedPatient) {
    return (
      <div className="flex flex-col w-full max-w-5xl mx-auto">
        <h1 className="font-bold text-[28px] text-ktx-purple mt-8">Customer Support</h1>
        <p className="mt-6 text-sm text-gray-500">Loading patient...</p>
      </div>
    );
  }

  if (!selectedPatient) {
    return (
      <div className="flex flex-col w-full max-w-5xl mx-auto">
        <h1 className="font-bold text-[28px] text-ktx-purple mt-8">Customer Support</h1>
        <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {lookupError || "Patient not found."}
        </p>
        <Link
          to={supportHomePath("patient-id")}
          className="mt-4 text-ktx-purple hover:underline font-medium text-sm w-fit"
        >
          Back to search
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col w-full max-w-5xl mx-auto">
        <h1 className="font-bold text-[28px] text-ktx-purple mt-8">Customer Support</h1>

        <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-gray-300 bg-slate-50 px-3 py-2 text-sm shadow-sm">
          <p className="text-gray-800 truncate min-w-0">
            <span className="font-medium">{selectedPatient.description || "Patient"}</span>
            <span className="text-gray-500"> · ID {selectedPatient.patient_id}</span>
            {selectedPatient.mrn && (
              <span className="text-gray-500"> · MRN {selectedPatient.mrn}</span>
            )}
          </p>
          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={handleRefreshPatient}
              disabled={isRefreshing}
              className="inline-flex items-center justify-center rounded-md p-1.5 text-ktx-purple hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-ktx-purple disabled:opacity-50"
              title="Reload patient"
              aria-label="Reload patient"
            >
              <RefreshCw
                className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
                aria-hidden
              />
            </button>
            <button
              type="button"
              className="text-ktx-purple hover:underline font-medium"
              onClick={handleChangePatient}
            >
              {backLabel}
            </button>
          </div>
        </div>

        {lookupError && (
          <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {lookupError}
          </p>
        )}

        <PatientDetails
          patient={selectedPatient}
          supportData={supportData}
          refreshKey={patientRefreshKey}
          onPatientUpdate={(updates) =>
            setSelectedPatient((prev) => (prev ? { ...prev, ...updates } : prev))
          }
        />

        <div className="mt-4 mb-8">
          {!share?.loading && (
            <SupportAgentChat
              key={share?.data?.session_id || selectedPatient.patient_id}
              seedContext={chatSeedContext}
              suggestions={CHAT_SUGGESTIONS}
              intro={
                isCapabilityEnabled("smartChart")
                  ? "The agent already knows this patient, their doctor, validator results, and Smart Chart flags — ask about the pipeline, the note, additional context, or what to tell the doctor."
                  : "The agent already knows this patient, their doctor, and the validator results — ask about the pipeline, the note, the transcript, or what to tell the doctor."
              }
              placeholder="e.g. Why is the note missing for this patient?"
              shareable
              restored={
                share?.data
                  ? {
                      sessionId: share.data.session_id,
                      conversation: share.data.ui?.conversation || [],
                      resolved: share.data.ui?.resolved || { patients: [], doctors: [], tickets: [] },
                    }
                  : undefined
              }
            />
          )}
        </div>
      </div>

      <SupportFloatingActions
        patientId={selectedPatient?.patient_id ?? null}
        trace={supportData.trace}
        traceLoading={supportData.loading.trace}
        traceError={supportData.fetchErrors.trace}
      />
    </>
  );
};

export default SupportPatientPage;
